import { useState, useEffect } from 'react'
import { useCallStore } from '../stores/callStore'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

interface CallHistoryItem {
  id: string
  sessionId: string
  customerName: string | null
  startedAt: number
  duration: number
  status: 'in_progress' | 'completed' | 'missed' | 'abandoned'
  hasBooking: boolean
}

interface CallHistorySidebarProps {
  onCallSelect?: () => void // Callback when a call is selected (e.g., to switch to live view)
}

export function CallHistorySidebar({ onCallSelect }: CallHistorySidebarProps) {
  const [calls, setCalls] = useState<CallHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const { isCallActive, currentSessionId, loadSession, isLoadingSession } = useCallStore()

  useEffect(() => {
    fetchCallHistory()
    // Refresh every 30 seconds
    const interval = setInterval(fetchCallHistory, 30000)
    return () => clearInterval(interval)
  }, [])

  async function fetchCallHistory() {
    try {
      const response = await fetch(`${API_URL}/api/calls/history?limit=50`)
      const data = await response.json()
      setCalls(data.calls || [])
    } catch (err) {
      console.error('Failed to fetch call history:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleCallClick(call: CallHistoryItem) {
    // Load the session data
    await loadSession(call.sessionId)
    // Notify parent to switch to live view if needed
    if (onCallSelect) {
      onCallSelect()
    }
  }

  function groupByDate(callList: CallHistoryItem[]) {
    const groups: Record<string, CallHistoryItem[]> = {}

    callList.forEach(call => {
      // startedAt is in seconds, convert to milliseconds
      const timestamp = call.startedAt > 9999999999 ? call.startedAt : call.startedAt * 1000
      const date = new Date(timestamp)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      let label = ''
      if (date.toDateString() === today.toDateString()) {
        label = 'Today'
      } else if (date.toDateString() === yesterday.toDateString()) {
        label = 'Yesterday'
      } else {
        label = date.toLocaleDateString('en-GB', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        })
      }

      if (!groups[label]) groups[label] = []
      groups[label].push(call)
    })

    return groups
  }

  const groupedCalls = groupByDate(calls)

  function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  function formatTime(timestamp: number): string {
    // Handle both seconds and milliseconds timestamps
    const ts = timestamp > 9999999999 ? timestamp : timestamp * 1000
    return new Date(ts).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  function getStatusIcon(call: CallHistoryItem) {
    if (call.status === 'completed' && call.hasBooking) {
      return <span className="text-green-600 text-base" title="Booking created">&#10003;</span>
    }
    if (call.status === 'missed') {
      return <span className="text-amber-600 text-base" title="Missed call">&#9888;</span>
    }
    if (call.status === 'abandoned') {
      return <span className="text-red-600 text-base" title="Call abandoned">&#10005;</span>
    }
    if (call.status === 'completed') {
      return <span className="text-blue-500 text-base" title="Completed">&#10003;</span>
    }
    return null
  }

  const isSelected = (call: CallHistoryItem) => 
    currentSessionId === call.sessionId || currentSessionId === call.id

  return (
    <aside className="w-64 bg-gray-50 border-r border-gray-200 h-full overflow-y-auto flex-shrink-0">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 sticky top-0 bg-gray-50 z-10">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Call History
        </h2>
      </div>

      {/* Active call indicator */}
      {isCallActive && (
        <div className="bg-red-50 border-b border-red-200 p-3">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-medium text-red-700">Active Now</span>
          </div>
        </div>
      )}

      {/* Loading session indicator */}
      {isLoadingSession && (
        <div className="bg-blue-50 border-b border-blue-200 p-3">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-xs font-medium text-blue-700">Loading call...</span>
          </div>
        </div>
      )}

      {/* Call list */}
      {loading ? (
        <div className="p-4 text-center">
          <div className="animate-pulse text-gray-400">Loading...</div>
        </div>
      ) : calls.length === 0 ? (
        <div className="p-4 text-center text-gray-500 text-sm">
          No calls yet
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {Object.entries(groupedCalls).map(([date, dateCalls]) => (
            <div key={date}>
              {/* Date header */}
              <div className="px-4 py-2 bg-gray-100 sticky top-14 z-5">
                <h3 className="text-xs font-medium text-gray-600">{date}</h3>
              </div>

              {/* Calls for this date */}
              {dateCalls.map(call => (
                <button
                  key={call.id}
                  onClick={() => handleCallClick(call)}
                  disabled={isLoadingSession}
                  className={`
                    w-full px-4 py-3 text-left transition-colors duration-150
                    ${isSelected(call) 
                      ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                      : 'hover:bg-gray-100 border-l-4 border-l-transparent'
                    }
                    ${isLoadingSession ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        isSelected(call) ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {call.customerName 
                          ? call.customerName 
                          : `Call at ${formatTime(call.startedAt)}${call.duration > 0 ? ` (${formatDuration(call.duration)})` : ''}`
                        }
                      </p>
                      {call.customerName && (
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTime(call.startedAt)}
                          {call.duration > 0 && (
                            <span className="ml-2">
                              ({formatDuration(call.duration)})
                            </span>
                          )}
                        </p>
                      )}
                    </div>

                    <div className="ml-2 flex-shrink-0">
                      {getStatusIcon(call)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Load more button */}
      {calls.length >= 50 && (
        <button
          className="w-full p-3 text-sm text-blue-600 hover:bg-gray-100 border-t border-gray-200"
          onClick={() => {/* Load more logic */}}
        >
          Load More
        </button>
      )}
    </aside>
  )
}
